import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import api, { getImageUrl } from '../api';
import { productSlug } from '../utils/slug';
import { useAuth } from '../context/AuthContext';

const STEPS = ['pending', 'confirmed', 'processing', 'shipped', 'delivered'];

const STEP_LABELS = {
  pending:    { label: 'Order Placed',  icon: '🛒' },
  confirmed:  { label: 'Confirmed',     icon: '✅' },
  processing: { label: 'Processing',    icon: '📦' },
  shipped:    { label: 'Shipped',       icon: '🚚' },
  delivered:  { label: 'Delivered',     icon: '🎉' },
};

function getTrackingUrl(carrier, number) {
  if (!number) return null;
  const c = (carrier || '').toLowerCase();
  if (c === 'usps') return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${number}`;
  if (c === 'fedex') return `https://www.fedex.com/apps/fedextrack/?tracknumbers=${number}`;
  if (c === 'ups') return `https://www.ups.com/track?tracknum=${number}`;
  if (number.startsWith('1Z')) return `https://www.ups.com/track?tracknum=${number}`;
  if (/^(94|93|92|95)\d/.test(number)) return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${number}`;
  return `https://www.google.com/search?q=track+package+${number}`;
}

export default function OrderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [returnModal, setReturnModal] = useState(false);
  const [returnForm, setReturnForm] = useState({ reason: '', details: '' });
  const [returnSubmitting, setReturnSubmitting] = useState(false);
  const [returnSuccess, setReturnSuccess] = useState(false);
  const [returnError, setReturnError] = useState('');
  const [existingReturn, setExistingReturn] = useState(null);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    api.get(`/orders/${id}`).then(r => {
      setOrder(r.data);
      setLoading(false);
    }).catch(() => { navigate('/orders'); });
    api.get('/returns').then(r => {
      const match = r.data.returns.find(ret => ret.order_id === parseInt(id));
      if (match) setExistingReturn(match);
    }).catch(() => {});
  }, [id, user, navigate]);

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this order? This cannot be undone.')) return;
    setCancelling(true);
    setCancelError('');
    try {
      await api.put(`/orders/${id}/status`, { status: 'cancelled' });
      setOrder(prev => ({ ...prev, status: 'cancelled' }));
    } catch (e) {
      setCancelError(e.response?.data?.error || 'Failed to cancel order. Please contact support.');
    } finally {
      setCancelling(false);
    }
  };

  const handleReturnSubmit = async (e) => {
    e.preventDefault();
    setReturnSubmitting(true);
    setReturnError('');
    try {
      const r = await api.post('/returns', { order_id: parseInt(id), ...returnForm });
      setExistingReturn(r.data);
      setReturnSuccess(true);
      setReturnModal(false);
    } catch (err) {
      setReturnError(err.response?.data?.error || 'Failed to submit return request.');
    } finally {
      setReturnSubmitting(false);
    }
  };

  if (loading) return <div className="spinner" />;
  if (!order) return null;

  const isCancelled = order.status === 'cancelled';
  const currentStep = isCancelled ? -1 : STEPS.indexOf(order.status);
  const shippingAddr = order.shipping_address ? JSON.parse(order.shipping_address) : null;
  const daysSince = (Date.now() - new Date(order.created_at).getTime()) / 86400000;
  const canReturn = order.status === 'delivered' && daysSince <= 30 && !existingReturn;
  const RETURN_REASONS = [
    'Item arrived damaged', 'Wrong item received', 'Item not as described',
    'Changed my mind', 'Size/fit issue', 'Quality not as expected', 'Other',
  ];

  return (
    <div className="container" style={{ maxWidth: 860 }}>
      <div style={{ marginBottom: 20 }}>
        <Link to="/orders">← Back to My Orders</Link>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ margin: 0 }}>Order #{order.id}</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: '#6b7280' }}>
            Placed on {new Date(order.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </span>
          {['pending', 'confirmed'].includes(order.status) && (
            <button
              onClick={handleCancel}
              disabled={cancelling}
              style={{
                padding: '8px 18px', borderRadius: 8, border: '1.5px solid #ef4444',
                background: 'white', color: '#ef4444', fontWeight: 600, fontSize: 14,
                cursor: cancelling ? 'not-allowed' : 'pointer', opacity: cancelling ? 0.6 : 1,
              }}
            >
              {cancelling ? 'Cancelling…' : 'Cancel Order'}
            </button>
          )}
          {canReturn && (
            <button
              onClick={() => setReturnModal(true)}
              style={{
                padding: '8px 18px', borderRadius: 8, border: '1.5px solid #f59e0b',
                background: 'white', color: '#b45309', fontWeight: 600, fontSize: 14, cursor: 'pointer',
              }}
            >
              ↩️ Request Return
            </button>
          )}
          {existingReturn && (
            <span style={{
              padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
              background: existingReturn.status === 'approved' ? '#d1fae5' : existingReturn.status === 'rejected' ? '#fee2e2' : '#fef3c7',
              color: existingReturn.status === 'approved' ? '#065f46' : existingReturn.status === 'rejected' ? '#991b1b' : '#92400e',
            }}>
              Return {existingReturn.status === 'pending' ? 'Pending Review' : existingReturn.status === 'approved' ? '✓ Approved' : '✗ Declined'}
            </span>
          )}
        </div>
      </div>
      {cancelError && (
        <div style={{ background: '#fee2e2', color: '#991b1b', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: 14 }}>
          {cancelError}
        </div>
      )}
      {returnSuccess && (
        <div style={{ background: '#d1fae5', color: '#065f46', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 14, fontWeight: 600 }}>
          ✅ Return request submitted! We'll review it within 1–2 business days and email you the outcome.
        </div>
      )}

      {/* Tracking timeline */}
      {!isCancelled ? (
        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', padding: '28px 32px', marginBottom: 24 }}>
          <h3 style={{ marginBottom: 24 }}>Order Tracking</h3>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', position: 'relative' }}>
            {/* progress line */}
            <div style={{
              position: 'absolute', top: 20, left: '10%', right: '10%', height: 3,
              background: '#e5e7eb', zIndex: 0,
            }} />
            <div style={{
              position: 'absolute', top: 20, left: '10%', height: 3,
              width: currentStep <= 0 ? '0%' : `${(currentStep / (STEPS.length - 1)) * 80}%`,
              background: 'var(--primary)', zIndex: 1, transition: 'width 0.4s ease',
            }} />

            {STEPS.map((step, i) => {
              const done = i <= currentStep;
              const active = i === currentStep;
              return (
                <div key={step} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 2, flex: 1 }}>
                  <div style={{
                    width: 42, height: 42, borderRadius: '50%',
                    background: done ? 'var(--primary)' : '#f3f4f6',
                    border: active ? '3px solid var(--primary)' : done ? 'none' : '2px solid #d1d5db',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 18, marginBottom: 10,
                    boxShadow: active ? '0 0 0 4px rgba(79,70,229,0.15)' : 'none',
                  }}>
                    {done ? (active ? STEP_LABELS[step].icon : '✓') : STEP_LABELS[step].icon}
                  </div>
                  <span style={{ fontSize: 12, fontWeight: done ? 700 : 400, color: done ? 'var(--primary)' : '#9ca3af', textAlign: 'center' }}>
                    {STEP_LABELS[step].label}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Show tracking number when shipped */}
          {order.tracking_number && (
            <div style={{ marginTop: 24, padding: '14px 16px', background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>🚚 Tracking Information</div>
              <div style={{ fontSize: 14, color: '#374151' }}>
                {order.tracking_carrier && <span style={{ marginRight: 8, fontWeight: 600 }}>{order.tracking_carrier}</span>}
                <span style={{ fontFamily: 'monospace' }}>{order.tracking_number}</span>
              </div>
              <a
                href={getTrackingUrl(order.tracking_carrier, order.tracking_number)}
                target="_blank" rel="noopener noreferrer"
                className="btn btn-sm btn-primary"
                style={{ marginTop: 10, display: 'inline-flex' }}
              >
                Track Package →
              </a>
            </div>
          )}
        </div>
      ) : (
        <div style={{ background: '#fee2e2', borderRadius: 12, padding: '16px 24px', marginBottom: 24, color: '#991b1b', fontWeight: 600 }}>
          ❌ This order has been cancelled.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20, alignItems: 'start' }}>
        {/* Items */}
        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', padding: 20 }}>
          <h3 style={{ marginBottom: 16 }}>Items Ordered</h3>
          {order.items?.map(item => {
            const img = getImageUrl(item.image_url);
            return (
              <div key={item.id} style={{ display: 'flex', gap: 16, padding: '12px 0', borderBottom: '1px solid #f3f4f6' }}>
                {img ? (
                  <img src={img} alt={item.product_name} style={{ width: 70, height: 70, objectFit: 'cover', borderRadius: 8 }} />
                ) : (
                  <div style={{ width: 70, height: 70, background: '#f3f4f6', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>📦</div>
                )}
                <div style={{ flex: 1 }}>
                  <Link to={`/product/${productSlug(item.product_name, item.product_id)}`} style={{ fontWeight: 600, fontSize: 14 }}>{item.product_name}</Link>
                  <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
                    Qty: {item.quantity} × ${item.price.toFixed(2)}
                  </div>
                </div>
                <div style={{ fontWeight: 700 }}>${item.total.toFixed(2)}</div>
              </div>
            );
          })}
        </div>

        {/* Summary + Address */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Price summary */}
          <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', padding: 20 }}>
            <h3 style={{ marginBottom: 16 }}>Order Summary</h3>
            {[
              ['Subtotal', `$${order.subtotal?.toFixed(2) || '—'}`],
              ['Shipping', order.shipping === 0 ? 'FREE' : `$${order.shipping?.toFixed(2) || '—'}`],
              ['Tax', `$${order.tax?.toFixed(2) || '—'}`],
            ].map(([label, val]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: 8 }}>
                <span style={{ color: '#6b7280' }}>{label}</span><span>{val}</span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 17, borderTop: '1px solid #e5e7eb', paddingTop: 12, marginTop: 4 }}>
              <span>Total</span><span>${order.total?.toFixed(2)}</span>
            </div>
          </div>

          {/* Shipping address */}
          {shippingAddr && (
            <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', padding: 20 }}>
              <h3 style={{ marginBottom: 12 }}>Shipping To</h3>
              <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.8 }}>
                <div>{shippingAddr.first_name} {shippingAddr.last_name}</div>
                <div>{shippingAddr.address}</div>
                {shippingAddr.address2 && <div>{shippingAddr.address2}</div>}
                <div>{shippingAddr.city}, {shippingAddr.state} {shippingAddr.zip}</div>
                {shippingAddr.phone && <div>{shippingAddr.phone}</div>}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Return Request Modal */}
      {returnModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16,
        }}>
          <div style={{ background: 'white', borderRadius: 16, padding: 32, maxWidth: 480, width: '100%' }}>
            <h2 style={{ marginBottom: 6 }}>↩️ Request a Return</h2>
            <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 24 }}>Order #{id} — within 30-day return window</p>
            <form onSubmit={handleReturnSubmit}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 6 }}>Reason *</label>
                <select
                  required
                  value={returnForm.reason}
                  onChange={e => setReturnForm(f => ({ ...f, reason: e.target.value }))}
                  style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 14 }}
                >
                  <option value="">Select a reason...</option>
                  {RETURN_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 6 }}>Additional Details (optional)</label>
                <textarea
                  value={returnForm.details}
                  onChange={e => setReturnForm(f => ({ ...f, details: e.target.value }))}
                  placeholder="Describe the issue in more detail..."
                  style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 14, minHeight: 100, resize: 'vertical', boxSizing: 'border-box' }}
                />
              </div>
              {returnError && (
                <div style={{ background: '#fee2e2', color: '#991b1b', borderRadius: 8, padding: '10px 12px', marginBottom: 16, fontSize: 13 }}>
                  {returnError}
                </div>
              )}
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" className="btn btn-outline" style={{ flex: 1 }} onClick={() => setReturnModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={returnSubmitting}>
                  {returnSubmitting ? 'Submitting…' : 'Submit Return Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
